// SpecRef: ARCH-003
// See: docs/decisions/ARCH-003-wind-heatmap-color-themes.md#themes
// All wind heatmap color theme definitions.

import type { SystemThemeId, WindColorTheme } from './types';

// ── Theme definitions ─────────────────────────────────────────────────────────

const legacy: WindColorTheme = {
  id: 'legacy',
  name: 'Legacy',
  description:
    'Original color scheme (gray → purple → blue → green → orange → red)',
  gradient: [
    [0, '#e6e6e6'],
    [7.5, '#d9a9de'],
    [17, '#ae7ed5'],
    [18, '#6174c2'],
    [20, '#238990'],
    [22, '#1da747'],
    [24, '#61a116'],
    [26, '#b97407'],
    [28, '#c53c09'],
    [30, '#940211'],
  ],
};

const noaa: WindColorTheme = {
  id: 'noaa',
  name: 'NOAA Standard',
  description:
    'Based on NWS operational wind graphics (blue → yellow → red → dark red)',
  gradient: [
    [0, '#4575b4'],
    [3, '#70a5cd'],
    [6, '#9fcee3'],
    [9, '#cde8ed'],
    [12, '#efe7be'],
    [15, '#fdc97d'],
    [18, '#f99858'],
    [22, '#ec603d'],
    [26, '#cf2c2a'],
    [30, '#a50026'],
  ],
};

const viridis: WindColorTheme = {
  id: 'viridis',
  name: 'Viridis',
  description:
    'Perceptually uniform, colorblind-safe (dark purple → teal → yellow)',
  gradient: [
    [0, '#440154'],
    [3, '#462372'],
    [6, '#404284'],
    [9, '#355e8c'],
    [12, '#2b778e'],
    [15, '#248f8c'],
    [18, '#3dae78'],
    [22, '#7ccf50'],
    [26, '#bede2f'],
    [30, '#fee825'],
  ],
};

const inferno: WindColorTheme = {
  id: 'inferno',
  name: 'Inferno',
  description:
    'High-contrast for dark backgrounds (black → purple → red → yellow)',
  gradient: [
    [0, '#000004'],
    [3, '#1b0c41'],
    [6, '#4a0c6b'],
    [9, '#781c6d'],
    [12, '#a52c60'],
    [15, '#cf4446'],
    [18, '#ed6925'],
    [22, '#fb9b06'],
    [26, '#f7d13d'],
    [30, '#fcffa4'],
  ],
};

const turbo: WindColorTheme = {
  id: 'turbo',
  name: 'Turbo',
  description:
    'Improved rainbow — familiar weather-map look with better perceptual uniformity',
  gradient: [
    [0, '#30123b'],
    [3, '#4662d7'],
    [6, '#36aaf9'],
    [9, '#1ae4b6'],
    [12, '#72fe5e'],
    [15, '#c8ef34'],
    [18, '#faba39'],
    [22, '#f66b19'],
    [26, '#ca2a04'],
    [30, '#7a0403'],
  ],
};

const plasma: WindColorTheme = {
  id: 'plasma',
  name: 'Plasma',
  description:
    'Perceptually uniform, colorblind-safe (blue → purple → pink → yellow)',
  gradient: [
    [0, '#0d0887'],
    [3, '#43049c'],
    [6, '#7105a4'],
    [9, '#99189b'],
    [12, '#bb3686'],
    [15, '#d6576d'],
    [18, '#eb7954'],
    [22, '#f89f3c'],
    [26, '#f9ca29'],
    [30, '#f0f921'],
  ],
};

const cmoceanSpeed: WindColorTheme = {
  id: 'cmocean_speed',
  name: 'Ocean Speed',
  description:
    'Oceanographic speed palette — perceptually uniform (dark green → light yellow)',
  gradient: [
    [0, '#001a0a'],
    [3, '#003013'],
    [6, '#00481d'],
    [9, '#016328'],
    [12, '#108334'],
    [15, '#31a440'],
    [18, '#61c24e'],
    [22, '#96db61'],
    [26, '#ccf082'],
    [30, '#fdffb6'],
  ],
};

const tealRose: WindColorTheme = {
  id: 'teal_rose',
  name: 'Teal & Rose',
  description: 'Diverging palette — deep teal → white → rose → purple',
  gradient: [
    [0, '#0b3948'],
    [3, '#1a6b6a'],
    [6, '#3a9a8c'],
    [9, '#7ec4a5'],
    [12, '#c8e6cb'],
    [15, '#f5ebe0'],
    [18, '#f0c4b4'],
    [22, '#d9817a'],
    [26, '#b84b5a'],
    [30, '#6b2248'],
  ],
};

const focc: WindColorTheme = {
  id: 'focc',
  name: 'FOCC Ops',
  description:
    'Impact-based flight ops scale — gray (calm) → cyan → blue → gold → red → maroon',
  gradient: [
    [0, '#b0b8bf'],
    [2.5, '#8cc5cf'],
    [5, '#5ba3b5'],
    [8, '#2978a0'],
    [12, '#1a5c8a'],
    [15, '#d4b86a'],
    [18, '#d98a3c'],
    [22, '#c44e2a'],
    [26, '#8b1a1a'],
    [30, '#4a0e0e'],
  ],
};

// ── Export ─────────────────────────────────────────────────────────────────────

const coolSpectral: WindColorTheme = {
  id: 'cool_spectral',
  name: 'Cool Spectral',
  description:
    'Full hue rotation — purple → blue → green → gold → rose → #9c489c (35+ m/s)',
  gradient: [
    [0, '#6271b7'],
    [1, '#39619f'],
    [3, '#4a94a9'],
    [5, '#4d8d7b'],
    [7, '#53a553'],
    [9, '#359f35'],
    [11, '#a79d51'],
    [13, '#9f7f3a'],
    [15, '#a16c5c'],
    [17, '#813a4e'],
    [19, '#af5088'],
    [21, '#754a93'],
    [24, '#6d61a3'],
    [27, '#44698d'],
    [29, '#5c9098'],
    [36, '#7d44a5'],
    [46, '#e7d7d7'],
    [51, '#dbd487'],
    [77, '#cdca70'],
    [104, '#808080'],
  ],
};

export const WIND_COLOR_THEMES: Record<SystemThemeId, WindColorTheme> = {
  legacy,
  noaa,
  viridis,
  inferno,
  turbo,
  plasma,
  // eslint-disable-next-line camelcase
  cmocean_speed: cmoceanSpeed,
  // eslint-disable-next-line camelcase
  teal_rose: tealRose,
  focc,
  // eslint-disable-next-line camelcase
  cool_spectral: coolSpectral,
};

export const SYSTEM_THEME_IDS = Object.keys(
  WIND_COLOR_THEMES,
) as SystemThemeId[];

export const DEFAULT_THEME_ID: SystemThemeId = 'cool_spectral';

export const DEFAULT_THEME: WindColorTheme =
  WIND_COLOR_THEMES[DEFAULT_THEME_ID];
